/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import requestAccount from "@stoneage/App/Handlers/Data/RequestAccountHandler";
import requestSystemList from "@stoneage/App/Handlers/Data/RequestSystemListHandler";
import parse from "html-react-parser";
import React, { useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    roleInformation = "roleInformation",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.roleInformation]: string;
    };
}
const mapDispatchToProps = {
    requestAccount,
    signIn,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
    loadCurrentCountries,
    requestSystemList,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    countries: getCurrentCountries(state),
    homepageUrl: getHomePageUrl(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface RequestAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    userInformationGridItem?: GridItemProps;
    userInformationTypography?: TypographyProps;
    emailGridItem?: GridItemProps;
    userInformationFieldsGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    requestAccountGridItem?: GridItemProps;
    checkboxWrapperGridItem?: GridItemProps;
    statePostalCondeGritItem?: GridItemProps;
    requestAccountTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    termsOfUseGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    roleSelectHelpIcon?: IconPresentationProps;
    roleSelectLabelStyledWrapper?: InjectableCss;
    countrySelect?: SelectProps;
    CheckboxWrapper?: InjectableCss;
    additionalInformationCheckbox?: CheckboxPresentationProps;
    selectGritItem: GridItemProps;
    informationalTextTypography: TypographyProps;
    additionalCompanyInfoTypography: TypographyProps;
    additionalCompanyInfoGridItem: GridItemProps;
}

export const requestAccountStyles: RequestAccountStyles = {
    informationalTextTypography: {
        as: "p",
        css: css`
            margin-bottom: 32px;
            line-height: 26px;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 15px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userInformationGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    userInformationTypography: {
        css: css`
            color: ${getColor("common.ui1")};
            font-size: 20px;
            font-weight: bold;
            line-height: 24px;
        `,
    },
    userGridContainer: {
        css: css``,
    },
    emailGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    userInformationFieldsGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    requestAccountGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    checkboxWrapperGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-top: 0;
        `,
    },
    statePostalCondeGritItem: {
        width: [6, 6, 3, 3, 3],
    },
    requestAccountTextField: {
        css: css``,
    },
    selectGritItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            position: relative;
        `,
    },
    countrySelect: {
        cssOverrides: {
            formInputWrapper: css``,
        },
    },
    additionalCompanyInfoGridItem: {
        width: 12,
        css: css`
            &&& {
                border-top: 1px solid ${getColor("common.ui6")};
                padding: 20px 0 5px;
                margin: 15px 15px 0 15px;
                font-weight: 500;
            }
        `,
    },
    additionalCompanyInfoTypography: {
        as: "h2",
        css: css`
            color: ${getColor("common.ui1")};
            font-size: 20px;
            font-weight: bold;
            line-height: 24px;
        `,
    },
    CheckboxWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
        `,
    },
    additionalInformationCheckbox: {
        mergeCss: true,
        css: css`
            margin-bottom: 4px;
        `,
    },
    subscriptionGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    termsOfUseGridItem: {
        width: 12,
        css: css`
            &&& {
                border-top: 1px solid ${getColor("common.ui6")};
                padding-left: 0;
                padding-top: 30px;
                margin-left: 15px;
            }
        `,
    },
    roleSelectHelpIcon: {
        src: HelpCircle,
        size: 14,
        css: css`
            cursor: pointer;
            position: absolute;
            left: 115px;
            z-index: 2;
        `,
    },
    roleSelectLabelStyledWrapper: {
        css: css`
            max-width: fit-content;
            margin-right: 3px;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 15px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    requirementsTitle: {
        variant: "h2",
        as: "h2",
    },
};

const styles = requestAccountStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");

const RequestAccount = ({
    requestAccount,
    validateReCaptcha,
    countries,
    loadCurrentCountries,
    homepageUrl,
    requestSystemList,
    fields,
}: Props) => {
    const getRequiredErrorMessage = (displayName: string) => siteMessage("Field_Required", displayName);
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [companyNumber, setCompanyNumber] = useState("");
    const [phone, setPhone] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [role, setRole] = useState("");
    const [roles, setRoles] = useState<any>([]);
    const [selectedCountry, setSelectedCountry] = useState<any>(undefined);
    const [states, setStates] = useState<any>([]);
    const [selectedState, setSelctedState] = useState<any>([]);

    useEffect(() => {
        loadCurrentCountries();
        requestSystemList({
            slug: "UserRoleList",
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    setRoles(resultProps.apiResult?.result?.values);
                }
            },
        });
    }, []);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    const [firstNameError, setFirstNameError] = useState(getRequiredErrorMessage("First Name"));
    const [lastNameError, setLastNameError] = useState(getRequiredErrorMessage("Last Name"));
    const [companyNameError, setCompanyNameError] = useState(getRequiredErrorMessage("Company Name"));
    const [roleError, setRoleError] = useState(getRequiredErrorMessage("User Role"));

    const [phoneError, setPhoneError] = useState("");
    const [cityError, setCityError] = useState(getRequiredErrorMessage("City"));
    const [countryError, setCountryError] = useState(getRequiredErrorMessage("Country"));
    const [stateError, setStateError] = useState(getRequiredErrorMessage("State"));

    const [errorMessage, setErrorMessage] = useState("");
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isTaxExempt, setIsTaxExempt] = useState(false);
    const [applyForCredit, setApplyForCredit] = useState(false);
    const [acceptTermsOfUse, setAcceptTermsOfUse] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const toasterContext = useContext(ToasterContext);

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateFirstName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("First Name") : "";
        setFirstNameError(errorMessage as any);
        return !errorMessage;
    };
    const validateLastName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("Last Name") : "";
        setLastNameError(errorMessage as any);
        return !errorMessage;
    };
    const validateCompanyName = (name: string) => {
        const errorMessage = !name ? getRequiredErrorMessage("Company Name") : "";
        setCompanyNameError(errorMessage as any);
        return !errorMessage;
    };
    const validateRole = (role: string) => {
        const errorMessage = !role ? getRequiredErrorMessage("User Role") : "";
        setRoleError(errorMessage as any);
        return !errorMessage;
    };
    const validatePhone = (phone: string) => {
        let errorMessage;
        if (phone.trim().length > 0 && !/^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/g.test(phone)) {
            errorMessage = siteMessage("AddressInfo_Phone_Validation") as any;
        } else {
            errorMessage = "";
        }
        setPhoneError(errorMessage);
        return !errorMessage;
    };

    const validateCity = (city: string) => {
        const errorMessage = !city ? getRequiredErrorMessage("City") : "";
        setCityError(errorMessage as any);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            emailError ||
            lastNameError ||
            firstNameError ||
            companyNameError ||
            phoneError ||
            countryError ||
            (states.length && stateError) ||
            cityError ||
            roleError
        );
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const firstNameChangeHandler = (firstName: string) => {
        validateFirstName(firstName);
        setFirstName(firstName);
    };

    const lastNameChangeHandler = (lastName: string) => {
        validateLastName(lastName);
        setLastName(lastName);
    };
    const companyNameChangeHandler = (companyName: string) => {
        validateCompanyName(companyName);
        setCompanyName(companyName);
    };
    const phoneChangeHandler = (phone: string) => {
        validatePhone(phone);
        setPhone(phone);
    };
    const companyNumberChangeHandler = (companyNumber: string) => {
        setCompanyNumber(companyNumber);
    };
    const address1ChangeHandler = (address1: string) => {
        setAddress1(address1);
    };
    const address2ChangeHandler = (address2: string) => {
        setAddress2(address2);
    };
    const handleCountryChange = (event: any) => {
        const country = countries?.find(c => c.id === event.target.value);
        setSelectedCountry(country);
        setCountryError("");
    };
    const roleChangeHandler = (role: string) => {
        validateRole(role);
        setRole(role);
    };
    const handleStateChange = (event: any) => {
        const state = selectedCountry?.states?.find((s: any) => s.id === event.target.value);
        setSelctedState(state);
        if (!state?.name) {
            setStateError(getRequiredErrorMessage("State"));
        } else {
            setStateError("");
        }
    };
    const postalCodeChangeHandler = (postalCode: string) => {
        setPostalCode(postalCode);
    };
    const cityChangeHandler = (city: string) => {
        validateCity(city);
        setCity(city);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account requested successfully!"), messageType: "success" });
        setTimeout(() => {
            window.location.href = homepageUrl;
        }, 500);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isSubmitted) {
            setIsSubmitted(true);
        }
        if (!validateSubmitEnabled()) {
            return;
        }

        if (!acceptTermsOfUse) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "RequestAccount",
            recaptchaRef,
        });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);

        requestAccount({
            email,
            firstName,
            lastName,
            companyName,
            customerNumber: companyNumber,
            phone,
            country: selectedCountry?.name,
            address1,
            address2,
            city,
            state: selectedState?.name || "",
            postalCode,
            role,
            isTaxExempt,
            applyForCredit,
            signUpForNewsLetter: isSubscribed,
            dataShareOptOut: false,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    onSuccessHandler();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <Typography {...styles.informationalTextTypography}>
                {siteMessage("Custom_RequestAccount_InformationalText")}
            </Typography>
            <GridContainer {...styles.userGridContainer}>
                <GridItem {...styles.userInformationGridItem}>
                    <Typography {...styles.userInformationTypography}>{translate("User Information")}</Typography>
                </GridItem>
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        {...styles.emailTextField}
                        type="email"
                        label={translate("Email Address")}
                        placeholder={translate("Email@Address.com")}
                        onChange={e => emailChangeHandler(e.currentTarget.value)}
                        value={email}
                        data-test-selector="requestAccount_email"
                        required
                        error={isSubmitted && emailError}
                    />
                </GridItem>
                <GridItem {...styles.userInformationFieldsGridItem}>
                    <TextField
                        {...styles.requestAccountTextField}
                        label={translate("First Name")}
                        placeholder={translate("User's First Name")}
                        value={firstName}
                        data-test-selector="requestAccount_firstName"
                        onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && firstNameError}
                        required
                    />
                </GridItem>
                <GridItem {...styles.userInformationFieldsGridItem}>
                    <TextField
                        {...styles.requestAccountTextField}
                        label={translate("Last Name")}
                        placeholder={translate("User's Last Name")}
                        value={lastName}
                        data-test-selector="requestAccount_lastName"
                        onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && lastNameError}
                        required
                    />
                </GridItem>
                <GridItem {...styles.userInformationFieldsGridItem}>
                    <TextField
                        {...styles.requestAccountTextField}
                        label={translate("Company Name")}
                        placeholder={translate("Company Name")}
                        value={companyName}
                        data-test-selector="requestAccount_companyName"
                        onChange={e => companyNameChangeHandler(e.currentTarget.value)}
                        error={isSubmitted && companyNameError}
                        required
                    />
                </GridItem>
                <GridItem {...styles.userInformationFieldsGridItem}>
                    <TextField
                        {...styles.requestAccountTextField}
                        label={translate("Customer Number")}
                        placeholder={translate("Customer Number")}
                        value={companyNumber}
                        data-test-selector="requestAccount_companyNumber"
                        onChange={e => companyNumberChangeHandler(e.currentTarget.value)}
                        maxLength={50}
                    />
                </GridItem>
                <GridItem {...styles.additionalCompanyInfoGridItem}>
                    <Typography {...styles.additionalCompanyInfoTypography}>
                        {translate("Additional Company Details")}
                    </Typography>
                </GridItem>
                <GridItem width={[12, 12, 8, 8, 8]}>
                    <GridContainer>
                        <GridItem {...styles.requestAccountGridItem}>
                            <TextField
                                {...styles.requestAccountTextField}
                                label={translate("Phone")}
                                type="phone"
                                placeholder={translate("Phone Number")}
                                value={phone}
                                data-test-selector="requestAccount_phone"
                                onChange={e => phoneChangeHandler(e.currentTarget.value)}
                                error={phone.length > 0 && phoneError}
                                maxLength={15}
                            />
                        </GridItem>

                        {countries && countries.length > 1 && (
                            <GridItem {...styles.selectGritItem}>
                                <Select
                                    {...styles.countrySelect}
                                    label="Country"
                                    value={selectedCountry?.id || ""}
                                    required
                                    onChange={handleCountryChange}
                                    data-test-selector="requestAccount_country"
                                    error={isSubmitted && countryError}
                                >
                                    <option value="">{translate("Select Country")}</option>
                                    {countries.map(co => (
                                        <option key={co.id} value={co.id}>
                                            {co.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        <GridItem {...styles.requestAccountGridItem}>
                            <TextField
                                {...styles.requestAccountTextField}
                                label={translate("Address1")}
                                placeholder={translate("Address Line 1")}
                                value={address1}
                                data-test-selector="requestAccount_Address1"
                                onChange={e => address1ChangeHandler(e.currentTarget.value)}
                            />
                        </GridItem>
                        <GridItem {...styles.requestAccountGridItem}>
                            <TextField
                                {...styles.requestAccountTextField}
                                label={translate("Address2")}
                                placeholder={translate("Address Line 2")}
                                value={address2}
                                data-test-selector="requestAccount_Address1"
                                onChange={e => address2ChangeHandler(e.currentTarget.value)}
                            />
                        </GridItem>

                        <GridItem {...styles.requestAccountGridItem}>
                            <TextField
                                {...styles.requestAccountTextField}
                                name="City"
                                label="City"
                                placeholder={translate("City Name")}
                                value={city}
                                required
                                maxLength={256}
                                onChange={e => cityChangeHandler(e.currentTarget.value)}
                                error={isSubmitted && cityError}
                                data-test-selector="requestAccount_city"
                            />
                        </GridItem>
                        {states.length > 0 && (
                            <GridItem {...styles.statePostalCondeGritItem}>
                                <Select
                                    {...styles.countrySelect}
                                    label="State"
                                    value={selectedState?.id || ""}
                                    required
                                    onChange={handleStateChange}
                                    error={states.length > 0 && isSubmitted && stateError}
                                    data-test-selector="requestAccount_state"
                                >
                                    <option value="">{translate("Select State")}</option>
                                    {states?.map((st: any) => (
                                        <option key={st.id} value={st.id}>
                                            {st.name}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        <GridItem {...styles.statePostalCondeGritItem}>
                            <TextField
                                {...styles.requestAccountTextField}
                                label={translate("ZIP/Postal")}
                                placeholder={translate("ZIP/Postal")}
                                value={postalCode}
                                data-test-selector="requestAccount_postalCode"
                                onChange={e => postalCodeChangeHandler(e.currentTarget.value)}
                            />
                        </GridItem>
                        <GridItem {...styles.selectGritItem}>
                            <RoleSelectIcon fields={fields} />
                            <Select
                                {...styles.countrySelect}
                                label={translate("Assign User Role")}
                                onChange={e => roleChangeHandler(e.target.value)}
                                value={role}
                                data-test-selector="createUserModal_userRoleSelect"
                                required
                                error={isSubmitted && roleError}
                            >
                                <option value="">{translate("Select User Role")}</option>
                                {roles &&
                                    roles.map(({ name, description }: any) => (
                                        <option key={name} value={name}>
                                            {description}
                                        </option>
                                    ))}
                            </Select>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.additionalCompanyInfoGridItem}>
                    <Typography {...styles.additionalCompanyInfoTypography}>
                        {translate("Additional Information")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.checkboxWrapperGridItem}>
                    <StyledWrapper {...styles.CheckboxWrapper}>
                        <CheckboxGroup>
                            <Checkbox
                                {...styles.additionalInformationCheckbox}
                                onChange={(e, value) => setIsTaxExempt(value)}
                                checked={isTaxExempt}
                                data-test-selector="requestAccount_taxExepmt"
                            >
                                {siteMessage("Custom_RequestAccount_TaxExempt")}
                            </Checkbox>
                            <Checkbox
                                {...styles.additionalInformationCheckbox}
                                onChange={(e, value) => setApplyForCredit(value)}
                                checked={applyForCredit}
                                data-test-selector="requestAccount_applyForCredit"
                            >
                                {siteMessage("Custom_RequestAccount_ApplyForCredit")}
                            </Checkbox>
                            <Checkbox
                                {...styles.additionalInformationCheckbox}
                                onChange={(e, value) => setIsSubscribed(value)}
                                checked={isSubscribed}
                                data-test-selector="requestAccount_Subscription"
                            >
                                {siteMessage("SignIn_Sign_Up_Newsletter")}
                            </Checkbox>
                        </CheckboxGroup>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.termsOfUseGridItem}>
                    <CheckboxGroup>
                        <Checkbox
                            onChange={(e, value) => setAcceptTermsOfUse(value)}
                            checked={acceptTermsOfUse}
                            data-test-selector="requestAccount_acceptTermsOfUse"
                        >
                            {siteMessage("Custom_RequestAccount_TermsOfUse")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                <GridItem {...styles.reCaptchaGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="other-RequestAccount" />
                </GridItem>
                {errorMessage && (
                    <GridItem data-test-selector="requestAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                        <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                    </GridItem>
                )}

                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={isSubmitting || (isSubmitted && !isSubmitEnabled) || !acceptTermsOfUse}
                        data-test-selector="requestAccount_createButton"
                    >
                        {translate("I Accept & Submit Details")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};
const RoleSelectIcon = function ({ fields }: any) {
    const [roleInformationModalIsOpen, setRoleInformationModalIsOpen] = useState(false);

    return (
        <>
            <Icon {...styles.roleSelectHelpIcon} onClick={() => setRoleInformationModalIsOpen(true)} />
            <Modal
                headline={translate("User Roles")}
                isOpen={roleInformationModalIsOpen}
                handleClose={() => setRoleInformationModalIsOpen(false)}
            >
                {parse(`${fields.roleInformation}`, parserOptions)}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestAccount),
    definition: {
        displayName: "Request An Account",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.roleInformation,
                displayName: "Role Information",
                editorTemplate: "RichTextField",
                defaultValue: `
                <div>
                    <h5>User Administrator: </h5>
                    <ul>
                        <li>Full access to My Account. </li>
                        <li>Can order over budget.</li>
                        <li>Can see invoices and orders for users that User Administrator is assigned to.</li>
                        <li>Default approver if none is assigned to a user.</li>
                        <li>Can approve requisitions.</li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 3:</h5>
                    <ul>
                        <li>Can order over budget without approval.</li>
                        <li>Can see orders and invoices.</li>
                        <li>Cannot access User Administration or Budget Management. </li>
                        <li>Can approve requisitions. </li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 2:</h5>
                    <ul>
                        <li>Over budget orders require approval.</li>
                        <li>Can see orders.</li>
                        <li>Cannot see invoices.</li>
                        <li>Cannot access User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>
                <div>
                    <h5>Buyer Level 1:</h5>
                    <ul>
                        <li>Cannot be assigned as an approver.</li>
                        <li>All orders require approval.</li>
                        <li>Can see orders.</li>
                        <li>Cannot see invoices.</li>
                        <li>Cannot access Order Approval, User Administration, Budget Management, or Requisition Approval. </li>
                    </ul>
                </div>`,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
